/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import * as moment from 'moment';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { DatePickerRangeComponent, DatepickerRangeOptions, ModalConfig, ModalRef, ModalService } from '@wfm/core';
import { SearchCriteriaType, SearchParamConversionKeys, SearchParams } from '@app/common/types/search-params';
import { TranslateService } from '@ngx-translate/core';
import { AdvanceSearchService } from '@app/common/services/advance-search/advance-search.service';
import { FilterTag, RemovedFilterTag } from '@core/lib/components/filter-tag-panel/filter-tag-panel.component';
import { appConstants } from '@app/common/app-constants';
import { TextValidatorService } from '@app/common/services/validators/text-validator.service';
import { AdvSearchFieldDef } from '@app/common/types/adv-search-field-def';
import { SearchParamsError } from '@app/common/types/search-params-error';
import { isEmpty, isUndefined } from '@core/lib/helpers/helpers';
import { CustomWorkListService } from '@app/ui/common/work-list-navigator/custom-work-list.service';
import { UserWorkListParams } from '@app/common/types/user-work-list-params';
import { MessageService } from '@core/lib/services';
import { SearchList } from '@app/data-model/list/search-list';
import { Subscription } from 'rxjs';
import { DatabaseList } from '@app/data-model/list/database-list';
import { WorkListHeaderDataService } from '@app/ui/common/work-list-header-data.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { AppConfigService } from '@app/common/app-config.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { ListContainerService } from '@app/ui/list-container/list-container.service';
import { DOCUMENT } from '@angular/common';
import { AdvanceSearchFilters } from '@app/common/services/advance-search/advance-search-filters';
import { UtilService } from '@app/util/util.service';
import { LayoutService } from '@app/ui/common/layout.service';
import { RemoteArchivesService } from '@app/ui/search/remote-archives.service';

@Component({
  selector: 'app-advance-search',
  templateUrl: './advance-search.component.html',
  styleUrls: ['./advance-search.component.scss']
})
export class AdvanceSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  private type: string;
  private scrollToElementId: string;
  private _isActionButtonDisabled: boolean;
  private worklist: DatabaseList;
  private window: Window;
  isLoading = true;
  dateTimePref = {} as any;
  isRemoteRepositoriesSelected = false;

  isAdvanceSearch = true;
  canSaveAsWorkList = false;
  preloadFilters: AdvanceSearchFilters = new AdvanceSearchFilters();
  searchParams: SearchParams;
  searchParamsError: SearchParamsError = {};
  filterTags: FilterTag[] = [];

  acceptBtnLabel: string;
  rejectBtnLabel: string;
  isNhiEnabled = false;
  isTruePacs = false;
  isNHIPatternEmpty = false;

  @ViewChildren('inputElement') inputElements: QueryList<ElementRef>;
  @ViewChild('advanceSearchContainer') advanceSearchContainer: ElementRef<HTMLElement>;
  @ViewChild('advanceSearchInputs') advanceSearchInputs: ElementRef<HTMLElement>;
  @ViewChild('remoteRepoWarning') remoteRepoWarning: ElementRef<HTMLElement>;
  @ViewChild('filterTagPanel') filterTagPanel: ElementRef<HTMLElement>;
  @ViewChild('dobDatePickerComponent') dobElement: DatePickerRangeComponent;
  @ViewChild('preferenceTabContainer') preferenceTabContainer: ElementRef<HTMLElement>;

  constructor(
    public config: ModalConfig,
    public modalRef: ModalRef,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: any,
    private translateService: TranslateService,
    private messageService: MessageService,
    private advanceSearchService: AdvanceSearchService,
    private textValidatorService: TextValidatorService,
    private customWorkListService: CustomWorkListService,
    private modalService: ModalService,
    private workListHeaderDataService: WorkListHeaderDataService,
    private userPreferenceService: UserPreferenceService,
    private appConfigService: AppConfigService,
    private remoteArchivesService: RemoteArchivesService,
    private userPrivilegesService: UserPrivilegesService,
    private listContainerService: ListContainerService,
    private layoutService: LayoutService,
    private utilService: UtilService
  ) {
    this.window = (this.document as Document).defaultView;
  }

  ngOnInit(): void {
    this.type = this.config.data.type;
    this.worklist = this.config.data.list;
    this.preInitialization();
    this.searchParams = this.isAdvanceSearch
      ? new SearchParams(SearchCriteriaType.ADVANCED_SEARCH)
      : new SearchParams(SearchCriteriaType.CUSTOM_WL);
    this.isLoading = false;

    this.canSaveAsWorkList = this.userPrivilegesService.userCanCreateCustomWorklist();
    this.isRemoteRepositoriesSelected = !this.remoteArchivesService.isSearchLocalOnly();
    this.isNhiEnabled = this.appConfigService.isNHIEnabled();
    this.isTruePacs = this.appConfigService.getAppConfigByKey('cpacsVersion') === 'v8';
    this.dateTimePref = this.appConfigService.getDateTimePref();
    this.isNHIPatternEmpty = UtilService.isEmpty(this.appConfigService.getNHIPattern());
  }

  resized(event, filterTagPanelHeight: number) {
    const repoWarningHeight = this.remoteRepoWarning?.nativeElement?.offsetHeight
      ? this.remoteRepoWarning?.nativeElement?.offsetHeight
      : 0;
    const height =
      this.advanceSearchContainer?.nativeElement?.offsetHeight - (filterTagPanelHeight + repoWarningHeight + 22);
    this.renderer.setStyle(this.advanceSearchInputs.nativeElement, 'max-height', `${height}px`);
    if (this.advanceSearchContainer?.nativeElement?.offsetHeight) {
      this.renderer.setStyle(
        this.preferenceTabContainer.nativeElement,
        'height',
        `${this.advanceSearchContainer.nativeElement.offsetHeight + 22}px`
      );
    }
  }

  ngOnDestroy(): void {
    if (this.filterTagTimeout) {
      this.window.clearTimeout(this.filterTagTimeout);
    }
  }

  ngAfterViewInit(): void {
    this.loadFilters();
  }

  private loadFilters() {
    this.isLoading = !this.advanceSearchService.isFiltersReady;
    if (this.advanceSearchService.isFiltersReady) {
      this.loadAfterViewInit();
    } else {
      const subscription: Subscription = this.messageService.on('preloadSearchCriteria.done').subscribe(() => {
        this.isLoading = !this.advanceSearchService.isFiltersReady;
        this.loadAfterViewInit();
        subscription.unsubscribe();
      });
    }
  }

  private loadAfterViewInit() {
    const originalFilters = this.advanceSearchService.getFilters();

    this.preloadFilters = { ...originalFilters };

    const instituteSeparationEnabled = this.appConfigService.isInstituteSeparationEnabled();
    const accessibleInstituteIds = this.userPrivilegesService.institutes.map(institute => institute.id);
    if (instituteSeparationEnabled) {
      this.preloadFilters.instituteList = originalFilters.instituteList;
      this.preloadFilters.instituteList.forEach(institute => {
        institute.disabled = !accessibleInstituteIds.includes(institute.id);
      });
    }
    this.addFilterTag('timeOfExam', true);
    this.addFilterTag('verifiedDateTime', true);
    if (this.type === 'createWL') {
      //set verified exam as default status
      this.setDefaultExamStatus();
    } else if (this.type === 'editWL' && this.config.data.list && this.config.data.list.id) {
      this.isLoading = true;
      this.customWorkListService
        .getCustomWorklistFilter(this.config.data.list.id)
        .then((userWorkListParams: UserWorkListParams) => {
          const searchParams = new SearchParams(this.searchParams.searchCriteriaType);
          Object.keys(userWorkListParams).forEach(key => {
            if (userWorkListParams[key] !== null) {
              const searchParamType = SearchParamConversionKeys[key];
              searchParams[searchParamType.key] = this.advanceSearchService.getSearchParamFromValue(
                searchParamType.key,
                searchParamType.type,
                userWorkListParams[key]
              );
              this.setFilterTagsOnLoad(searchParamType.key, userWorkListParams[key]);
            }
          });
          if (userWorkListParams.patientBirthBeginDate && userWorkListParams.patientBirthEndDate) {
            this.setFilterTagsOnLoad('dob', userWorkListParams.patientBirthBeginDate);
          }
          this.setDateRangeTokens(userWorkListParams, searchParams);
          this.setMrnAndNhiValue(userWorkListParams, searchParams);
          searchParams.worklistName = userWorkListParams.worklistName || this.worklist.displayName;
          this.setFilterTagsOnLoad('worklistName', searchParams.worklistName);
          this.searchParams = searchParams;
          this.procedureDescStrSearch('');
          this.isLoading = false;
          this.cd.detectChanges();
        });
    } else if (this.advanceSearchService.advanceSearchParams) {
      this.loadSearchParams();
    }
    this.inputElements?.changes.subscribe(() => {
      if (!isUndefined(this.scrollToElementId)) {
        this.scrollTo(this.scrollToElementId);
        this.scrollToElementId = undefined;
      }
    });
    //doing a procedureDescStrSearch at init to remove the last search
    this.procedureDescStrSearch('');
    this.cd.detectChanges();
  }

  private loadSearchParams() {
    const searchParams = this.advanceSearchService.advanceSearchParams;
    if (!_.isEmpty(searchParams)) {
      if (!searchParams.advancedSearch) {
        if (!searchParams.verifiedDateTime) {
          searchParams.verifiedDateTime = this.searchParams.verifiedDateTime;
        }
        searchParams.timeOfExam = this.searchParams.timeOfExam;
      }
      searchParams.advancedSearch = this.isAdvanceSearch;
      searchParams.searchCriteriaType = SearchCriteriaType.ADVANCED_SEARCH;
      this.searchParams = searchParams;
      const keys = Object.keys(this.searchParams);
      keys.forEach(key => {
        if (key === 'dob') {
          this.dobValueChange();
        } else if (key === 'nhi' && !isEmpty(this.searchParams[key])) {
          this.setMrnOrNhiBasedOnFormat(this.searchParams[key], searchParams, key);
        } else {
          this.setFilterTagsOnLoad(key, this.searchParams[key]);
        }
      });
    }
  }

  private setMrnOrNhiBasedOnFormat(value, searchParams: SearchParams, key) {
    if (
      this.isNhiEnabled &&
      key === 'nhi' &&
      !UtilService.isEmpty(this.appConfigService.getNHIPattern()) &&
      this.utilService.isValidNHI(value, this.appConfigService.getNHIPattern())
    ) {
      searchParams.nhi = value;
      searchParams.mrn = '';
      this.setFilterTagsOnLoad('nhi', value);
    } else {
      searchParams.mrn = value;
      searchParams.nhi = '';
      this.setFilterTagsOnLoad('mrn', value);
    }
  }

  private setFilterTagsOnLoad(key: string, values: any) {
    //Apply preselected filter tags.
    if (values && !_.isEmpty(values) && appConstants.advanceSearchLabelMap[key]) {
      this.addFilterTag(key);
    }
  }

  private setMrnAndNhiValue(response, searchParams: SearchParams) {
    const patientIdentifier = response['patientIdentifier'];
    if (this.isNhiEnabled && patientIdentifier?.includes('nhi')) {
      searchParams.nhi = patientIdentifier.split(':')[1];
      this.setFilterTagsOnLoad('nhi', patientIdentifier);
    } else if (!patientIdentifier?.includes('nhi')) {
      searchParams.mrn = patientIdentifier;
      this.setFilterTagsOnLoad('mrn', patientIdentifier);
    }
  }

  private setDateRangeTokens(response, searchParams: SearchParams) {
    if (response['verifiedDateRangeToken']) {
      searchParams.verifiedDateTime = {
        startTime: undefined,
        endTime: undefined,
        option: appConstants.datePickerRangeValues.indexOf(response['verifiedDateRangeToken'])
      };
    }

    if (response['examDateRangeToken']) {
      searchParams.timeOfExam = {
        startTime: undefined,
        endTime: undefined,
        option: appConstants.datePickerRangeValues.indexOf(response['examDateRangeToken'])
      };
    }

    if (response['verifiedBeginDate'] && response['verifiedEndDate']) {
      searchParams.verifiedDateTime = {
        startTime: moment(response['verifiedBeginDate']),
        endTime: moment(response['verifiedEndDate']),
        option: DatepickerRangeOptions.customRange
      };
    }

    if (response['beginExamDateRange'] && response['endExamDateRange']) {
      searchParams.timeOfExam = {
        startTime: moment(response['beginExamDateRange']),
        endTime: moment(response['endExamDateRange']),
        option: DatepickerRangeOptions.customRange
      };
    }

    if (response['patientBirthBeginDate'] && response['patientBirthEndDate']) {
      searchParams.dob = {
        startTime: moment(response['patientBirthBeginDate']),
        endTime: moment(response['patientBirthEndDate']),
        option: DatepickerRangeOptions.customRange
      };
    }

    if (!response['examDateRangeToken'] && !response['beginExamDateRange'] && !response['endExamDateRange']) {
      searchParams.timeOfExam = {
        startTime: undefined,
        endTime: undefined,
        option: DatepickerRangeOptions.allDates
      };
    }
  }

  private preInitialization() {
    if (this.type === 'createWL') {
      this.isAdvanceSearch = false;
      this.acceptBtnLabel = this.translateService.instant('label.create');
      this.rejectBtnLabel = this.translateService.instant('label.cancel');
    } else if (this.type === 'editWL') {
      this.isAdvanceSearch = false;
      this.acceptBtnLabel = this.translateService.instant('label.save');
      this.rejectBtnLabel = this.translateService.instant('label.cancel');
    } else {
      this.isAdvanceSearch = true;
      this.acceptBtnLabel = this.translateService.instant('label.search');
      this.rejectBtnLabel = this.translateService.instant('searchModule.saveAsCustomWorklist');
    }
  }

  setDefaultExamStatus() {
    if (!this.isAdvanceSearch) {
      const verifiedStatus = this.preloadFilters.examStatusIds.find(examStatus => examStatus.examStatusNumber === 50);
      if (verifiedStatus) {
        this.searchParams.examStatusIds = [verifiedStatus];
        this.addFilterTag('examStatusIds');
      }
    }
  }

  cancelOrSave() {
    if (this.isAdvanceSearch) {
      // Assign Only selected items
      this.preloadFilters.descriptions = this.searchParams.descriptions;
      const saveModalSub: Subscription = this.listContainerService
        .openSaveAsWorkListModal(false, { searchParams: this.searchParams, searchParamsError: this.searchParamsError })
        .subscribe(resp => {
          if (resp === 'YES') {
            saveModalSub.unsubscribe();
            this.modalRef.close('user cancelled');
          }
        });
    } else {
      // create/edit WL
      this.modalRef.close('user cancelled');
    }
  }

  hasMoreHeaderContent(params) {
    const paramsCount: any = new TextEncoder().encode(JSON.stringify(params)).length;
    if (paramsCount > appConstants.searchFilterCharacterLimit) {
      this.modalService.openModal(this.utilService.filterCriteriaLimitTemplate, {
        headerContent: this.translateService.instant('searchCriteriaLimitTitle'),
        noButtonLabel: this.translateService.instant('label.close'),
        modalClass: 'modal--medium'
      });
      return true;
    }
    return false;
  }

  action() {
    if (this.type === 'createWL') {
      this.isLoading = true;
      const worklistParams = this.searchParams.buildUserDefinedWLParams();
      if (this.hasMoreHeaderContent(worklistParams)) {
        this.isLoading = false;
        return;
      }
      this.advanceSearchService
        .saveCustomWorklist(this.searchParams)
        .then(() => {
          this.isLoading = false;
          this.modalRef.close();
        })
        .catch(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        });
    } else if (this.type === 'editWL') {
      this.isLoading = true;
      const worklistParams = this.searchParams.buildUserDefinedWLParams();
      if (this.hasMoreHeaderContent(worklistParams)) {
        this.isLoading = false;
        return;
      }
      const clearInlineFilter = this.userPreferenceService.getUserPreferenceFromCache('clear-inline-filter');
      if (!clearInlineFilter && this.worklist.isFiltered) {
        this.modalService
          .openModal(this.translateService.instant('customWorklist.clearFilter'), {
            headerContent: this.translateService.instant('filtering.deleteFilterLabel'),
            modalClass: 'modal--medium',
            showChkBox: true,
            chkBoxValue: false,
            chkBoxTxt: this.translateService.instant('customWorklist.donotShowAgain'),
            enableYesBtn: true,
            isDecisionModal: true,
            yesButtonLabel: this.translateService.instant('label.clearall'),
            noButtonLabel: this.translateService.instant('label.cancel')
          })
          .afterClosed.subscribe(resp => {
            if (resp.result === 'YES') {
              this.workListHeaderDataService.deleteFilterAndSort(this.worklist.id, false);
              this.workListHeaderDataService.resetFilterParam(this.worklist.id);
              this.userPreferenceService.createOrUpdateUserPreference(
                'clear-inline-filter',
                resp.chkBoxValue.toString()
              );
              this.advanceSearchService
                .editCustomWorklist(this.worklist, worklistParams)
                .then(() => {
                  this.isLoading = false;
                  this.modalRef.close();
                })
                .catch(() => {
                  this.isLoading = false;
                  this.cd.detectChanges();
                });
            } else {
              this.isLoading = false;
              this.cd.detectChanges();
            }
          });
      } else {
        if (this.worklist.isFiltered) {
          this.workListHeaderDataService.deleteFilterAndSort(this.worklist.id, false);
        }
        this.advanceSearchService
          .editCustomWorklist(this.worklist, worklistParams)
          .then(() => {
            this.isLoading = false;
            this.modalRef.close();
          })
          .catch(() => {
            this.isLoading = false;
            this.cd.detectChanges();
          });
      }
    } else {
      this.searchParams.setPreferenceParams();
      this.advanceSearchService.advanceSearchParams = this.searchParams;

      const params = this.searchParams.buildRequestParams(
        SearchCriteriaType.ADVANCED_SEARCH,
        this.layoutService.getActiveWLColumnsAttrId()
      );
      if (this.hasMoreHeaderContent(params)) {
        return;
      } else {
        this.messageService.broadcast('list.display', {
          list: new SearchList(this.translateService.instant('search.results'), this.searchParams, {
            isAdvancedSearchList: true
          })
        });
        this.modalRef.close('advanceSearch');
      }
    }
  }

  procedureDescStrSearch(str: string) {
    if (str && str.trim().length > 3) {
      this.advanceSearchService.getProcedureDescriptions(str, true).then((descriptions: any[]) => {
        const descriptionValues = this.searchParams.descriptions?.length
          ? _.uniq(_.union(this.searchParams.descriptions, descriptions), false, item => {
              return item.id;
            })
          : [...descriptions];
        this.preloadFilters.descriptions = descriptionValues.sort(this.advanceSearchService.sortByNameProperty);
        this.cd.detectChanges();
      });
    } else if (this.searchParams.descriptions?.length) {
      const ids = _.pluck(this.searchParams.descriptions, 'id');
      this.advanceSearchService.getProcedureDescriptions(ids, false).then((descriptions: any[]) => {
        const descriptionValues = [...descriptions];
        this.preloadFilters.descriptions = descriptionValues.sort(this.advanceSearchService.sortByNameProperty);
        this.searchParams.descriptions = this.preloadFilters.descriptions;
        this.cd.detectChanges();
      });
    } else {
      this.preloadFilters.descriptions = [];
    }
  }

  scrollTo(key: string) {
    const find = this.inputElements.toArray().find(value => value.nativeElement.id === key);
    if (find) {
      find.nativeElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      this.scrollToElementId = key;
    }
  }

  get isActionButtonDisabled() {
    let hasError = false;
    const errorKeys = Object.keys(this.searchParamsError);
    for (let i = 0; i < errorKeys.length; i++) {
      if (this.searchParamsError[errorKeys[i]]) {
        hasError = true;
        break;
      }
    }
    this._isActionButtonDisabled = hasError || this.filterTags.length < 3;
    if (this.searchParams.advancedSearch) {
      return this._isActionButtonDisabled;
    } else if (this.type === 'editWL') {
      const clearInlineFilter = this.userPreferenceService.getUserPreferenceFromCache('clear-inline-filter');
      return (
        this._isActionButtonDisabled ||
        (clearInlineFilter && this.worklist.isFiltered) ||
        this.isLoading ||
        !this.searchParams.worklistName
      );
    } else {
      return this._isActionButtonDisabled || !this.searchParams.worklistName || this.isLoading;
    }
  }

  get isRejectButtonDisabled() {
    return this.searchParams.advancedSearch && this._isActionButtonDisabled;
  }

  isValidInput = (key: string): boolean => {
    const errorMsg = this.textValidatorService.validate(this.searchParams[key], AdvSearchFieldDef.get(key));
    this.searchParamsError[key] = errorMsg;
    return !errorMsg;
  };

  clearInputField(key: string) {
    this.searchParams[key] = undefined;
    this.searchParamsError[key] = undefined;
    this.removeFilterTag(key);
  }

  textValueChange(key: string) {
    if (this.isValidInput(key)) {
      if (_.isEmpty(this.searchParams[key])) {
        this.removeFilterTag(key);
        return;
      }
      if (appConstants.advanceSearchLabelMap[key]) {
        this.addFilterTag(key);
      }
    } else {
      const filterTag = this.filterTags.find(tag => tag.key === key);
      if (filterTag) {
        this.removeFilterTag(key, false);
      }
    }
  }

  dobValueChange() {
    if (this.searchParams.dob.startTime?.isValid() || this.searchParams.dob.endTime?.isValid()) {
      this.addFilterTag('dob');
    } else {
      this.removeFilterTag('dob');
    }
  }

  timeOfExamValueChange(value, property: string) {
    this.searchParams.timeOfExam[property] = value;
  }

  dropDownValueChange(key: string) {
    const values: any[] = this.searchParams[key];
    if (values && values.length) {
      this.addFilterTag(key);
    } else {
      this.removeFilterTag(key);
    }
  }

  filterTagPanelChange(removedFilterTag: RemovedFilterTag) {
    if (removedFilterTag.isRemoveAll) {
      this.resetPreLoadFilters();
      this.filterTags = [];
      const worklistName = this.searchParams.worklistName;
      this.searchParams = new SearchParams(this.searchParams.searchCriteriaType);
      this.searchParamsError = {};
      this.addFilterTag('timeOfExam', true);
      this.addFilterTag('verifiedDateTime', true);
      this.setDefaultExamStatus();
      if (!this.isAdvanceSearch && !isUndefined(worklistName) && !isEmpty(worklistName)) {
        this.searchParams.worklistName = worklistName;
        this.addFilterTag('worklistName', false);
      }
      this.resetDateTimeFilters();
      this.cd.detectChanges();
    } else {
      this.removeFilterTag(removedFilterTag.key);
    }
    if (
      removedFilterTag.key === 'descriptions' &&
      this.searchParamsError.timeOfExam === 'customWorklist.examDescLimit30DaysMsg'
    ) {
      this.searchParamsError.timeOfExam = undefined;
    }
  }

  private resetPreLoadFilters() {
    for (let i = 0; i < this.filterTags.length; i++) {
      if (this.searchParams[this.filterTags[i].key] instanceof Array) {
        this.resetPreLoadFilter(this.filterTags[i].key);
      }
    }
  }
  private resetPreLoadFilter(key) {
    this.preloadFilters[key] = [...this.preloadFilters[key]];
  }

  private resetDateTimeFilters() {
    this.dobElement?.clear();
    this.searchParams.timeOfExam = {
      startTime: undefined,
      endTime: undefined,
      option: DatepickerRangeOptions.lastWeek
    };
    this.searchParams.verifiedDateTime = {
      startTime: undefined,
      endTime: undefined,
      option: DatepickerRangeOptions.allDates
    };
    this.searchParams.dob = {
      startTime: undefined,
      endTime: undefined,
      option: DatepickerRangeOptions.customRange
    };
  }

  private removeFilterTag(key: string, resetValue = true) {
    this.filterTags = this.filterTags.filter(tag => tag.key !== key);
    if (resetValue) {
      const value: any = this.searchParams[key];
      if (value instanceof Array) {
        this.searchParams[key] = [];
        this.preloadFilters[key] = [...this.preloadFilters[key]];
      } else if (value && value.hasOwnProperty('startTime')) {
        this.searchParams[key] = {
          startTime: undefined,
          endTime: undefined,
          option: undefined
        };
        this.searchParamsError[key] = undefined;
      } else {
        this.searchParams[key] = undefined;
      }
    }
  }

  private filterTagTimeout;
  private addFilterTag(key: string, hideCloseButton = false) {
    const label = appConstants.advanceSearchLabelMap[key];
    if (label) {
      const filterTag = this.filterTags.find(tag => tag.key === key);
      if (!filterTag) {
        this.filterTags.push({
          key,
          label:
            key === 'nhi'
              ? this.utilService.getLocalizedNHISpecificText(label, this.appConfigService.getNHIType())
              : this.translateService.instant(label),
          hideCloseButton
        });
        this.filterTags = [...this.filterTags];
        // scroll to bottom when tag is added
        this.filterTagTimeout = this.window.setTimeout(() => {
          if (this.filterTagPanel?.nativeElement.scrollHeight > this.filterTagPanel?.nativeElement.clientHeight) {
            this.filterTagPanel.nativeElement.scrollTop =
              this.filterTagPanel?.nativeElement.scrollHeight - this.filterTagPanel?.nativeElement.clientHeight;
          }
          this.window.clearTimeout(this.filterTagTimeout);
        });
      }
    }
  }

  outSideClickDatePicker(key: string) {
    if (!this.searchParams[key]?.startTime && !this.searchParams[key]?.endTime) {
      const option = key === 'verifiedDateTime' ? DatepickerRangeOptions.allDates : DatepickerRangeOptions.lastWeek;
      this.searchParams[key] = {
        startTime: undefined,
        endTime: undefined,
        option
      };
      this.searchParamsError[key] = undefined;
      this.cd.detectChanges();
    }
  }

  setDateError(event, key) {
    this.searchParamsError[key] = event ? 'advancedSearch.error.invalidInputCharacters' : undefined;
  }
}
